import { ActionIcon, ActionIconProps, Tooltip } from "@mantine/core";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import { Editor } from "@tiptap/react";
import { EventName } from "core";
import { Fragment, useCallback, useState, type ReactNode } from "react";
import { useTrackEvent } from "../utils/useTrackEvent";
import { richTextExtensions } from "./RichTextEditor/extensions";

export function TextCopier({
  content,
  disabled = false,
  children,
  iconProps,
  iconSize = 14,
  copyFrom,
}: {
  content: string | (() => string);
  disabled?: boolean;
  children?: (copy: () => void, copied?: boolean) => ReactNode;
  iconProps?: ActionIconProps;
  iconSize?: number;
  /** Source from where the copy action originated e.g. "assistant_output_actions" */
  copyFrom?: string;
}) {
  const [copied, setCopied] = useState<boolean>(false);

  const { track } = useTrackEvent();

  const getContent = useCallback(() => {
    if (typeof content === "function") {
      return content();
    }

    return content;
  }, [content]);

  const copy = useCallback(() => {
    const resolvedContent = getContent();

    /**
     * Only create the editor if the user actually clicks the copy button; it's
     * cheaper to discard it on every use than to keep it around for each message.
     */
    const editor = new Editor({
      extensions: richTextExtensions,
      content: resolvedContent,
      contentType: "markdown",
    });

    const clipboardItem = new ClipboardItem({
      "text/html": new Blob([editor.getHTML()], {
        type: "text/html",
      }),
      "text/plain": new Blob([resolvedContent], {
        type: "text/plain",
      }),
    });
    navigator.clipboard.write([clipboardItem]);

    track(EventName.ClickedCopyToClipboardButton, {
      copyFrom,
    });

    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 1000);
  }, [copyFrom, getContent, track]);

  return (
    <Fragment>
      <Tooltip
        label={copied ? "Copied to your clipboard" : "Copy to clipboard"}
      >
        {children ? (
          children(copy, copied)
        ) : (
          <ActionIcon
            disabled={disabled}
            color={copied ? "green" : "dimmed"}
            variant="subtle"
            onClick={(e) => {
              e.stopPropagation();
              copy();
            }}
            {...(iconProps ?? {})}
          >
            {copied ? (
              <IconCheck size={iconSize} />
            ) : (
              <IconCopy size={iconSize} />
            )}
          </ActionIcon>
        )}
      </Tooltip>
    </Fragment>
  );
}
